/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMount } from 'react-use';

import { selectIsNewIa } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { WEBCHAT_CHANNEL_TYPE } from '../../../util/channelTypes';
import { HeaderNameInput } from './HeaderNameInput';
import { HeaderTabs } from './HeaderTabs';

import './TabbedHeader.scss';

interface TabbedHeaderProps {
  /**
   *
   */
  assistantId?: string;
  /**
   *
   */
  environmentId?: string;
  /**
   *
   */
  catalogItemId?: string;
  /**
   *
   */
  channelType?: string;
  /**
   *
   */
  integrationName?: string;
  /**
   *
   */
  updateIntegrationName?: (
    name: string,
    isValid?: boolean,
    isDirty?: boolean
  ) => void;
  /**
   *
   */
  onClickTab?: (index: number) => void;
  /**
   *
   */
  useSplashPage?: boolean;
  /**
   *
   */
  useQuickSetup?: boolean;
  /**
   *
   */
  loading?: boolean;
  /**
   *
   */
  readOnly?: boolean;
  /**
   *
   */
  Tabs: JSX.Element[];
  /**
   *
   */
  SplashPage?: JSX.Element;
  /**
   *
   */
  QuickSetup?: JSX.Element;
  /**
   *
   */
  defaultTab?: number;
}
export const TabbedHeader = ({
  assistantId,
  environmentId,
  catalogItemId,
  channelType,
  integrationName,
  updateIntegrationName,
  onClickTab,
  useSplashPage,
  useQuickSetup,
  loading,
  readOnly,
  SplashPage,
  QuickSetup,
  Tabs,
  defaultTab
}: TabbedHeaderProps) => {
  const isNewIa = useSelector(selectIsNewIa);
  const isWebChat = channelType === WEBCHAT_CHANNEL_TYPE;

  const scrollTop = useCallback(() => {
    const el = document.querySelector('#panel-root');
    if (el) {
      const content = el.querySelector('.Panel__content');
      content.scrollTop = 0;
    }
  }, []);

  const handleClickTab = useCallback(
    index => {
      // If passed in as a Prop, run that one first, then make sure scrollTop
      onClickTab?.(index);
      const tab = Tabs[index];
      if (isNewIa && tab?.props && isWebChat) {
        ctaEvents.webChatSettingsTab(
          {
            category: tab.props.tabname
          },
          {
            waEnvironmentId: tab.props.environmentId
          }
        );
      }
      scrollTop();
    },
    [isNewIa, isWebChat, onClickTab, scrollTop, Tabs]
  );

  useMount(() => {
    const tab = Tabs[defaultTab];
    if (isNewIa && tab?.props && isWebChat) {
      ctaEvents.webChatSettingsTab(
        {
          category: tab?.props.tabname
        },
        {
          waEnvironmentId: tab?.props.environmentId
        }
      );
    }
  });

  return (
    <>
      {!isNewIa && (
        <HeaderNameInput
          loading={loading}
          readOnly={readOnly}
          name={integrationName}
          updateName={updateIntegrationName}
        />
      )}
      {useSplashPage && (
        <section className="Panel--IntegrationContent bx--grid bx--no-gutter">
          {SplashPage}
        </section>
      )}
      {!useSplashPage && useQuickSetup && !_.isNil(QuickSetup) && (
        <section className="Panel--IntegrationContent bx--grid bx--no-gutter">
          {React.cloneElement(QuickSetup, {
            readOnly,
            assistantId,
            environmentId,
            catalogItemId
          })}
        </section>
      )}
      {!useSplashPage && !useQuickSetup && (
        <HeaderTabs
          assistantId={assistantId}
          environmentId={environmentId}
          catalogItemId={catalogItemId}
          loading={loading}
          Tabs={Tabs}
          defaultTab={defaultTab}
          onClickTab={handleClickTab}
          label={integrationName}
          readOnly={readOnly}
        />
      )}
    </>
  );
};
